import React, { useState } from 'react';
import { Card, Icon, Modal, Button, Input, Message, Loader } from 'semantic-ui-react';
import axios from 'axios';

const DRIP_EMAILS = [
    { step: 0, templateKey: 'welcome', subject: 'Welcome to Multiply!', timing: '15 minutes after activation', icon: 'star', color: '#0884a8' },
    { step: 1, templateKey: 'day1', subject: 'Master Your Elevator Pitch', timing: '1 day after activation', icon: 'bullhorn', color: '#27ae60' },
    { step: 2, templateKey: 'day3', subject: 'Level Up with the Casual Encounter', timing: '3 days after activation', icon: 'level up', color: '#f39c12' },
    { step: 3, templateKey: 'day7', subject: 'Best Practices for Your Training', timing: '7 days after activation', icon: 'book', color: '#8e44ad' },
    { step: 4, templateKey: 'day10', subject: 'Sharpen Your Defensive Skills', timing: '10 days after activation', icon: 'shield', color: '#2980b9' },
    { step: 5, templateKey: 'day14', subject: 'Consistency Is Your Competitive Edge', timing: '14 days after activation', icon: 'trophy', color: '#e74c3c' },
];

const DripEmailList = () => {
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewStep, setPreviewStep] = useState(null);
    const [previewHtml, setPreviewHtml] = useState('');
    const [previewLoading, setPreviewLoading] = useState(false);
    const [testEmailOpen, setTestEmailOpen] = useState(false);
    const [testStep, setTestStep] = useState(null);
    const [testEmail, setTestEmail] = useState('');
    const [sending, setSending] = useState(false);
    const [sendResult, setSendResult] = useState(null);

    const openPreview = async (step) => {
        setPreviewStep(step);
        setPreviewOpen(true);
        setPreviewLoading(true);
        setPreviewHtml('');
        try {
            const response = await axios.get(`/admin/emails/drip-preview?step=${step}`);
            setPreviewHtml(response.data.html || '<p>No preview available</p>');
        } catch (err) {
            setPreviewHtml(`<p style="padding:20px;color:#e74c3c;">Failed to load preview: ${err.message}</p>`);
        } finally {
            setPreviewLoading(false);
        }
    };

    const openTestEmail = (step) => {
        setTestStep(step);
        setTestEmailOpen(true);
        setSendResult(null);
        setTestEmail('');
    };

    const sendTest = async () => {
        if (!testEmail) return;
        setSending(true);
        setSendResult(null);
        try {
            await axios.post('/admin/emails/send-test', {
                type: 'drip',
                step: testStep,
                recipientEmail: testEmail,
            });
            setSendResult({ success: true, message: `Test email sent to ${testEmail}` });
        } catch (err) {
            setSendResult({ success: false, message: err.response?.data?.error || err.message });
        } finally {
            setSending(false);
        }
    };

    return (
        <div>
            <p style={{ color: '#445566', marginBottom: '20px', fontSize: '14px' }}>
                These automated drip emails are sent to new users after they activate their account.
                Each email is triggered based on time elapsed since activation.
            </p>

            <Card.Group itemsPerRow={3}>
                {DRIP_EMAILS.map((drip) => (
                    <Card key={drip.step} style={{ borderTop: `3px solid ${drip.color}`, borderRadius: '8px' }}>
                        <Card.Content>
                            <Card.Header style={{ color: '#2c3e50', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <Icon name={drip.icon} style={{ color: drip.color }} />
                                {drip.templateKey === 'welcome' ? 'Welcome Email' : `Day ${drip.templateKey.replace('day', '')}`}
                            </Card.Header>
                            <Card.Meta style={{ marginTop: '8px' }}>
                                <Icon name="clock" /> {drip.timing}
                            </Card.Meta>
                            <Card.Description style={{ marginTop: '12px', color: '#445566' }}>
                                <strong>Subject:</strong> {drip.subject}
                            </Card.Description>
                        </Card.Content>
                        <Card.Content extra>
                            <Button basic color="blue" size="small" onClick={() => openPreview(drip.step)}>
                                <Icon name="eye" /> Preview
                            </Button>
                            <Button basic color="green" size="small" onClick={() => openTestEmail(drip.step)} style={{ marginLeft: '8px' }}>
                                <Icon name="send" /> Send Test
                            </Button>
                        </Card.Content>
                    </Card>
                ))}
            </Card.Group>

            {/* Preview Modal */}
            <Modal open={previewOpen} onClose={() => setPreviewOpen(false)} size="large">
                <Modal.Header>
                    <Icon name="eye" style={{ color: '#0884a8' }} /> Email Preview - {
                        previewStep !== null && DRIP_EMAILS[previewStep]
                            ? DRIP_EMAILS[previewStep].subject
                            : ''
                    }
                </Modal.Header>
                <Modal.Content>
                    {previewLoading ? (
                        <div style={{ textAlign: 'center', padding: '60px' }}>
                            <Loader active inline="centered" content="Loading preview..." />
                        </div>
                    ) : (
                        <iframe
                            title="Email Preview"
                            srcDoc={previewHtml}
                            style={{ width: '100%', height: '600px', border: '1px solid #e0e0e0', borderRadius: '4px' }}
                            sandbox="allow-same-origin"
                        />
                    )}
                </Modal.Content>
                <Modal.Actions>
                    <Button onClick={() => setPreviewOpen(false)}>Close</Button>
                </Modal.Actions>
            </Modal>

            {/* Send Test Modal */}
            <Modal open={testEmailOpen} onClose={() => setTestEmailOpen(false)} size="small">
                <Modal.Header>
                    <Icon name="send" style={{ color: '#0884a8' }} /> Send Test Drip Email
                </Modal.Header>
                <Modal.Content>
                    <p style={{ color: '#445566' }}>
                        Send a test copy of "{testStep !== null && DRIP_EMAILS[testStep] ? DRIP_EMAILS[testStep].subject : ''}" to:
                    </p>
                    <Input
                        fluid
                        placeholder="email@example.com"
                        value={testEmail}
                        onChange={(e) => setTestEmail(e.target.value)}
                        type="email"
                    />
                    {sendResult && (
                        <Message positive={sendResult.success} negative={!sendResult.success} style={{ marginTop: '12px' }}>
                            {sendResult.message}
                        </Message>
                    )}
                </Modal.Content>
                <Modal.Actions>
                    <Button onClick={() => setTestEmailOpen(false)}>Cancel</Button>
                    <Button color="blue" onClick={sendTest} loading={sending} disabled={!testEmail || sending}>
                        <Icon name="send" /> Send Test
                    </Button>
                </Modal.Actions>
            </Modal>
        </div>
    );
};

export default DripEmailList;
